/**
 * Labstep
 *
 * @module containers/Entity/Update
 * @desc Container for updating an entity
 */

import type { EntityName } from 'labstep-web/services/schema/schema';
import { updateEntity } from 'labstep-web/state/actions/entity';
import {
  selectEntity,
  selectUpdateEntityStatus,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  EntityUpdateContainerChildrenProps,
  IEntityUpdateContainerProps,
} from './types';

export const EntityUpdateContainer = <
  T extends EntityName = EntityName,
>({
  children,
  entityName,
  id,
  batch,
  optimistic,
}: IEntityUpdateContainerProps<T>): React.ReactElement => {
  const dispatch = useDispatch();

  const entity = useSelector((state: any) => {
    if (batch || !id) {
      return undefined;
    }
    return selectEntity(state, entityName, id);
  });

  const status = useSelector((state: any) => {
    if (batch || !id) {
      return undefined;
    }
    return selectUpdateEntityStatus(state, entityName, id);
  });

  const update = useCallback(
    (body: Parameters<typeof updateEntity>[2], options: any = {}) => {
      let enhancedOptions = {
        ...options,
        optimistic,
      };
      if (batch) {
        enhancedOptions = { ...enhancedOptions, batch: true };
      }

      dispatch(updateEntity(entityName, id, body, enhancedOptions));
    },
    [dispatch, entityName, id, batch, optimistic],
  );

  const childrenProps: EntityUpdateContainerChildrenProps<T> = {
    update,
    entity,
    status,
  };

  return children(childrenProps) as React.ReactElement;
};
